import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Save, Loader2, MapPin } from "lucide-react";
import StateCountyPicker from "@/components/StateCountyPicker";
import { CompanyFacts } from "@/components/CompanyFacts";
import { BidDefaults } from "@/components/BidDefaults";
import { VoiceSetting } from "@/components/VoiceSetting";
import { TakeoffLegendSetting } from "@/components/TakeoffLegendSetting";
import { useAuth } from "@/context/AuthContext";

// Company-wide operational settings, kept off the proposal-branding page. The office/HQ
// location seeds mobilization's drive-distance origin on every bid.
export default function CompanySettings() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => { api.get("/branding").then((r) => setB(r.data || {})).catch(() => setB({})); }, []);

  if (!b) return <div className="p-8 text-sm text-slate-400">Loading company settings…</div>;
  const set = (k, v) => setB((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/branding", b); setB(r.data); toast.success("Company settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-4 sm:px-8 py-8 max-w-[1800px] w-full mx-auto" data-testid="company-settings-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#003E87]" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-black">Company settings</h1>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="company-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-6">Company-wide operational defaults. Your office / HQ location seeds mobilization on every bid so you never re-enter it.</p>
      <VoiceSetting />
      <TakeoffLegendSetting canEdit={["owner", "admin"].includes(user?.role)} />

      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400"><MapPin className="w-3.5 h-3.5" /> Office / HQ location</div>
        <p className="text-xs text-slate-500 -mt-1">Where crews mobilize from. The Mobilization tab defaults its drive-distance origin to this — estimators can override it per bid.</p>
        <div>
          <Label className="text-xs text-slate-500">Street address <span className="text-slate-300">(optional)</span></Label>
          <Input value={b.office_address || ""} onChange={(e) => set("office_address", e.target.value)} placeholder="1200 Industrial Blvd, Midland" data-testid="company-office-address" className="mt-1" />
        </div>
        <StateCountyPicker idPrefix="company-office" state={b.office_state || ""} county={b.office_county || ""}
          onChange={({ state, county }) => setB((x) => ({ ...x, office_state: state, office_county: county }))} />
        <div className="w-40">
          <Label className="text-xs text-slate-500">Zip <span className="text-slate-300">(optional)</span></Label>
          <Input value={b.office_zip || ""} onChange={(e) => set("office_zip", e.target.value)} placeholder="79701" data-testid="company-office-zip" className="mt-1 font-mono" />
        </div>
                <div className="mt-4 rounded-lg border border-slate-200 p-3 flex items-start gap-3" data-testid="gf-writes-documents">
          <input type="checkbox" id="gf-writes" checked={!!b.gridforge_writes_documents} onChange={(e) => set("gridforge_writes_documents", e.target.checked)} data-testid="gf-writes-documents-toggle" className="mt-1" />
          <label htmlFor="gf-writes" className="text-sm text-slate-700">
            <span className="font-semibold">GridForge writes documents</span> <span className="text-slate-400">— off</span>
            <span className="block text-xs text-slate-500 mt-0.5">Off (recommended): the packet is assembled only from documents your team filed — To the client, For the approver, For the team after award. On: GridForge also writes its own renders (portfolio proposal, brief, schedule, team plan, map, context).</span>
          </label>
        </div>
      </Card>
      <CompanyFacts />
      <BidDefaults />
    </div>
  );
}
